// @ts-check
// Python -> IR-JSON (DEC-107).
//
// ## Por qué un analizador propio y no el `ast` de Python en Pyodide
//
// Se consideró pedirle a Pyodide que analizara el código con su propio `ast`,
// que es el módulo que mejor conoce la gramática de Python. Se descartó por
// dos motivos, en este orden:
//
// 1. **Verificabilidad.** El mapeo AST->IR es la parte donde se cometen los
//    errores que importan (un bucle con una vuelta de menos, una rama
//    perdida), y meterlo dentro de un Worker con Pyodide lo deja fuera del
//    alcance de las pruebas rápidas. Este módulo es una función pura: se
//    prueba con texto de entrada y se compara la ejecución del resultado.
// 2. **Disponibilidad.** Pyodide tarda segundos en arrancar la primera vez.
//    Traducir es una acción que el alumno espera instantánea.
//
// El precio es que solo se entiende el SUBCONJUNTO de Python que este curso
// enseña. Ese precio se paga en voz alta: lo que no se entiende se rechaza
// diciendo qué es y en qué línea, nunca se traduce a medias ni se ignora.
//
// ## Qué se entiende
//
// Asignación (incluida `+=` y compañía), `print`, `input` con `int`/`float`/
// `str`, `if`/`elif`/`else`, `while`, `for ... in range(...)`, `def`/`return`,
// llamadas, listas y acceso por índice, aritmética, comparaciones y
// `and`/`or`/`not`, comentarios.

import { crearRegistroDeMotivos, traduccionFallida, traduccionOk } from './resultado.js';

/** Palabras clave que abren un bloque y se tratan aparte. */
const PALABRAS = new Set([
  'if', 'elif', 'else', 'while', 'for', 'def', 'return', 'in', 'and', 'or', 'not',
  'True', 'False', 'None', 'break', 'continue', 'pass', 'import', 'from', 'class',
  'try', 'except', 'finally', 'with', 'lambda', 'global', 'nonlocal', 'yield', 'raise',
  'assert', 'del', 'is',
]);

const COMPARADORES = /** @type {Record<string, string>} */ ({
  '==': '==', '!=': '!=', '<': '<', '<=': '<=', '>': '>', '>=': '>=',
});

/**
 * @typedef {{ tipo: string, valor: any, linea: number }} Token
 */

/**
 * Parte el código en líneas lógicas con su nivel de sangría.
 *
 * Python delimita bloques por sangría, así que el "tokenizado" empieza por
 * ahí. Se rechazan las tabulaciones mezcladas con espacios: es una fuente
 * clásica de programas que parecen una cosa y son otra, y adivinar cuál era
 * la intención sería justo lo que este módulo no debe hacer.
 * @param {string} codigo
 * @returns {{ ok: true, lineas: { nivel: number, texto: string, linea: number }[] } | { ok: false, motivo: string, linea: number }}
 */
function partirEnLineas(codigo) {
  /** @type {{ nivel: number, texto: string, linea: number }[]} */
  const lineas = [];
  const crudas = String(codigo ?? '').split('\n');
  /** @type {number[]} */
  const pila = [0];
  for (let i = 0; i < crudas.length; i += 1) {
    const cruda = crudas[i];
    if (cruda.trim() === '') continue;
    if (/^\s*#/.test(cruda)) {
      lineas.push({ nivel: pila[pila.length - 1], texto: cruda.trim(), linea: i + 1 });
      continue;
    }
    const sangria = cruda.match(/^[ \t]*/)?.[0] ?? '';
    if (sangria.includes('\t') && sangria.includes(' ')) {
      return { ok: false, motivo: 'La sangría mezcla tabuladores y espacios.', linea: i + 1 };
    }
    lineas.push({ nivel: sangria.replace(/\t/g, '    ').length, texto: cruda.trim(), linea: i + 1 });
  }
  return { ok: true, lineas };
}

/**
 * Tokeniza UNA línea de Python (sin sangría).
 * @param {string} texto
 * @param {number} linea
 * @returns {Token[]}
 */
function tokenizarLinea(texto, linea) {
  /** @type {Token[]} */
  const tokens = [];
  let i = 0;
  while (i < texto.length) {
    const c = texto[i];
    if (c === ' ' || c === '\t') { i += 1; continue; }
    if (c === '#') break;
    if (c === '"' || c === "'") {
      // Las f-strings se detectan por el prefijo justo antes de la comilla.
      let valor = '';
      i += 1;
      while (i < texto.length && texto[i] !== c) {
        if (texto[i] === '\\' && i + 1 < texto.length) {
          const siguiente = texto[i + 1];
          valor += siguiente === 'n' ? '\n' : siguiente === 't' ? '\t' : siguiente;
          i += 2;
          continue;
        }
        valor += texto[i];
        i += 1;
      }
      i += 1;
      tokens.push({ tipo: 'CADENA', valor, linea });
      continue;
    }
    if (/[0-9]/.test(c)) {
      let num = '';
      while (i < texto.length && /[0-9.]/.test(texto[i])) { num += texto[i]; i += 1; }
      tokens.push({ tipo: 'NUMERO', valor: Number(num), linea });
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      let id = '';
      while (i < texto.length && /[A-Za-z0-9_]/.test(texto[i])) { id += texto[i]; i += 1; }
      tokens.push({ tipo: PALABRAS.has(id) ? 'PALABRA' : 'ID', valor: id, linea });
      continue;
    }
    const dos = texto.slice(i, i + 2);
    if (['==', '!=', '<=', '>=', '+=', '-=', '*=', '/=', '//'].includes(dos)) {
      tokens.push({ tipo: 'OP', valor: dos, linea });
      i += 2;
      continue;
    }
    tokens.push({ tipo: 'OP', valor: c, linea });
    i += 1;
  }
  tokens.push({ tipo: 'FIN', valor: null, linea });
  return tokens;
}

/** Error de traducción con línea. */
class ErrorPython extends Error {
  /** @param {string} mensaje @param {number} linea */
  constructor(mensaje, linea) {
    super(mensaje);
    this.linea = linea;
  }
}

/** Analizador de expresiones sobre los tokens de UNA línea. */
class Expresiones {
  /** @param {Token[]} tokens */
  constructor(tokens) {
    this.tokens = tokens;
    this.i = 0;
  }
  get actual() { return this.tokens[this.i]; }
  avanzar() { return this.tokens[this.i++]; }
  /** @param {string} valor */
  esOp(valor) { return this.actual.tipo === 'OP' && this.actual.valor === valor; }
  /** @param {string} valor */
  esPalabra(valor) { return this.actual.tipo === 'PALABRA' && this.actual.valor === valor; }
  /** @param {string} valor */
  esperarOp(valor) {
    if (!this.esOp(valor)) throw new ErrorPython(`Falta "${valor}".`, this.actual.linea);
    return this.avanzar();
  }

  /** @returns {any} */
  expresion() { return this.o(); }

  /** @returns {any} */
  o() {
    /** @type {any} */
    let izq = this.y();
    while (this.esPalabra('or')) {
      this.avanzar();
      izq = { nodo: 'Operacion', operador: 'O', operandos: [izq, this.y()] };
    }
    return izq;
  }
  /** @returns {any} */
  y() {
    /** @type {any} */
    let izq = this.no();
    while (this.esPalabra('and')) {
      this.avanzar();
      izq = { nodo: 'Operacion', operador: 'Y', operandos: [izq, this.no()] };
    }
    return izq;
  }
  /** @returns {any} */
  no() {
    if (this.esPalabra('not')) {
      this.avanzar();
      return { nodo: 'Operacion', operador: 'NO', operandos: [this.no()] };
    }
    return this.comparacion();
  }
  /** @returns {any} */
  comparacion() {
    /** @type {any} */
    let izq = this.suma();
    if (this.actual.tipo === 'OP' && COMPARADORES[this.actual.valor]) {
      const operador = COMPARADORES[this.avanzar().valor];
      izq = { nodo: 'Operacion', operador, operandos: [izq, this.suma()] };
      // En Python `a == b == c` significa `a == b and b == c`, no
      // `(a == b) == c`, que es lo que daba juntarlas por la izquierda: el
      // triángulo 5, 5, 5 salía "otro" en el diagrama traducido (drift-reviewer,
      // 2026-09-11). Tampoco se reescribe como `and`: repetiría `b`, que puede
      // ser un `input()`. Traducir entero o no traducir (DEC-107).
      if (this.actual.tipo === 'OP' && COMPARADORES[this.actual.valor]) {
        throw new ErrorPython(
          'Las comparaciones encadenadas (como "a == b == c") no existen en los diagramas: ' +
            'escríbelas unidas con "and" (a == b and b == c).',
          this.actual.linea,
        );
      }
    }
    return izq;
  }
  /** @returns {any} */
  suma() {
    /** @type {any} */
    let izq = this.producto();
    while (this.actual.tipo === 'OP' && (this.actual.valor === '+' || this.actual.valor === '-')) {
      const operador = this.avanzar().valor;
      izq = { nodo: 'Operacion', operador, operandos: [izq, this.producto()] };
    }
    return izq;
  }
  /** @returns {any} */
  producto() {
    /** @type {any} */
    let izq = this.unario();
    while (
      this.actual.tipo === 'OP' &&
      (this.actual.valor === '*' || this.actual.valor === '/' || this.actual.valor === '%')
    ) {
      const operador = this.avanzar().valor;
      izq = { nodo: 'Operacion', operador, operandos: [izq, this.unario()] };
    }
    if (this.actual.tipo === 'OP' && this.actual.valor === '//') {
      throw new ErrorPython('La división entera "//" no existe en los diagramas.', this.actual.linea);
    }
    return izq;
  }
  /** @returns {any} */
  unario() {
    if (this.esOp('-')) {
      this.avanzar();
      const dentro = this.unario();
      // `-1` se colapsa a un literal negativo en vez de dejarlo como
      // "negación de 1". Además de producir una IR más limpia, es lo que
      // permite reconocer un paso negativo en `range(3, 0, -1)`: sin esto, el
      // traductor no vería que el bucle va hacia atrás y corregiría el límite
      // en el sentido contrario, alargando el bucle en silencio.
      if (dentro?.nodo === 'Literal' && typeof dentro.valor === 'number') {
        return { ...dentro, valor: -dentro.valor };
      }
      return { nodo: 'Operacion', operador: 'NEG', operandos: [dentro] };
    }
    if (this.esOp('+')) { this.avanzar(); return this.unario(); }
    return this.sufijo();
  }
  /** @returns {any} */
  sufijo() {
    let base = this.primaria();
    for (;;) {
      if (this.esOp('[')) {
        this.avanzar();
        const indice = this.expresion();
        this.esperarOp(']');
        base = { nodo: 'AccesoIndice', lista: base, indice };
        continue;
      }
      if (this.esOp('.')) {
        throw new ErrorPython('Los métodos (con punto) no existen en los diagramas.', this.actual.linea);
      }
      return base;
    }
  }
  /** @returns {any} */
  primaria() {
    const t = this.actual;
    if (t.tipo === 'NUMERO') {
      this.avanzar();
      return { nodo: 'Literal', valor: t.valor, tipo: Number.isInteger(t.valor) ? 'entero' : 'decimal' };
    }
    if (t.tipo === 'CADENA') {
      this.avanzar();
      return { nodo: 'Literal', valor: t.valor, tipo: 'cadena' };
    }
    if (t.tipo === 'PALABRA' && (t.valor === 'True' || t.valor === 'False')) {
      this.avanzar();
      return { nodo: 'Literal', valor: t.valor === 'True', tipo: 'booleano' };
    }
    if (this.esOp('(')) {
      this.avanzar();
      const dentro = this.expresion();
      this.esperarOp(')');
      return dentro;
    }
    if (this.esOp('[')) {
      throw new ErrorPython('Las listas literales no se pueden convertir todavía.', t.linea);
    }
    if (t.tipo === 'ID') {
      const nombre = this.avanzar().valor;
      if (this.esOp('(')) {
        this.avanzar();
        /** @type {any[]} */
        const argumentos = [];
        while (!this.esOp(')')) {
          argumentos.push(this.expresion());
          if (this.esOp(',')) this.avanzar();
          else break;
        }
        this.esperarOp(')');
        return { nodo: 'Llamada', nombre, argumentos };
      }
      return { nodo: 'ReferenciaVariable', nombre };
    }
    throw new ErrorPython(
      t.valor === null ? 'Falta algo al final de la línea.' : `No entiendo "${t.valor}".`,
      t.linea,
    );
  }
}

/**
 * @param {string} texto
 * @param {number} linea
 * @returns {any}
 */
function expresionDe(texto, linea) {
  const analizador = new Expresiones(tokenizarLinea(texto, linea));
  const resultado = analizador.expresion();
  if (analizador.actual.tipo !== 'FIN') {
    throw new ErrorPython(`Sobra "${analizador.actual.valor}" en la expresión.`, linea);
  }
  return resultado;
}

/** Conversión de tipo de una lectura: `int(input())` -> `entero`. */
const TIPO_DE_CONVERSION = /** @type {Record<string, string>} */ ({
  int: 'entero', float: 'decimal', str: 'cadena',
});

/**
 * Reconoce `input()`, `int(input())`, `float(input())`, `str(input())`.
 * @param {any} expresion
 * @returns {{ esLectura: boolean, tipo: string|null }}
 */
function comoLectura(expresion) {
  if (expresion?.nodo !== 'Llamada') return { esLectura: false, tipo: null };
  if (expresion.nombre === 'input') return { esLectura: true, tipo: null };
  const tipo = TIPO_DE_CONVERSION[expresion.nombre];
  const dentro = expresion.argumentos?.[0];
  if (tipo && dentro?.nodo === 'Llamada' && dentro.nombre === 'input') {
    return { esLectura: true, tipo };
  }
  return { esLectura: false, tipo: null };
}

/** @typedef {{ nivel: number, texto: string, linea: number }} LineaPython */

/**
 * Analiza el bloque ANIDADO que sigue a una cabecera (`if ...:`, `while ...:`).
 *
 * El nivel del bloque se DEDUCE de su primera línea en vez de suponer una
 * anchura de sangría fija. Python no obliga a usar cuatro espacios: exige
 * únicamente que todas las líneas de un mismo bloque estén al mismo nivel y
 * más adentro que su cabecera. Dar por hecho un ancho concreto haría que un
 * programa sangrado con dos espacios -- perfectamente válido -- se rechazara.
 * @param {LineaPython[]} lineas
 * @param {{ i: number }} cursor
 * @param {number} nivelPadre
 * @param {ReturnType<typeof crearRegistroDeMotivos>} registro
 * @param {number} lineaCabecera
 * @returns {any[]}
 */
function bloqueAnidado(lineas, cursor, nivelPadre, registro, lineaCabecera) {
  const primera = lineas[cursor.i];
  if (!primera || primera.nivel <= nivelPadre) {
    registro.anotar('Falta el contenido del bloque.', `línea ${lineaCabecera}`);
    return [];
  }
  return bloque(lineas, cursor, primera.nivel, registro);
}

/**
 * Analiza las líneas que están EXACTAMENTE al nivel indicado.
 * @param {LineaPython[]} lineas
 * @param {{ i: number }} cursor
 * @param {number} nivel
 * @param {ReturnType<typeof crearRegistroDeMotivos>} registro
 * @returns {any[]}
 */
function bloque(lineas, cursor, nivel, registro) {
  /** @type {any[]} */
  const sentencias = [];
  while (cursor.i < lineas.length && lineas[cursor.i].nivel >= nivel) {
    if (lineas[cursor.i].nivel > nivel) {
      registro.anotar('Hay una sangría inesperada.', `línea ${lineas[cursor.i].linea}`);
      cursor.i += 1;
      continue;
    }
    const antes = cursor.i;
    try {
      const s = unaSentencia(lineas, cursor, nivel, registro);
      if (Array.isArray(s)) sentencias.push(...s);
      else if (s) sentencias.push(s);
    } catch (error) {
      const e = /** @type {ErrorPython} */ (error);
      registro.anotar(e.message, `línea ${e.linea ?? lineas[antes].linea}`);
      if (cursor.i === antes) cursor.i += 1;
    }
    if (cursor.i === antes) cursor.i += 1;
  }
  return sentencias;
}

/**
 * @param {LineaPython[]} lineas
 * @param {{ i: number }} cursor
 * @param {number} nivel
 * @param {ReturnType<typeof crearRegistroDeMotivos>} registro
 * @returns {any}
 */
function unaSentencia(lineas, cursor, nivel, registro) {
  const { texto, linea } = lineas[cursor.i];

  if (texto.startsWith('#')) {
    cursor.i += 1;
    return { nodo: 'Comentario', texto: texto.replace(/^#\s?/, '') };
  }

  // --- Bloques ---
  const condicional = texto.match(/^(if|elif)\s+(.+):$/);
  if (condicional) {
    cursor.i += 1;
    const condicion = expresionDe(condicional[2], linea);
    const ramaSi = { nodo: 'Secuencia', sentencias: bloqueAnidado(lineas, cursor, nivel, registro, linea) };
    /** @type {any} */
    const resultado = { nodo: 'Condicional', condicion, rama_si: ramaSi };
    const siguiente = lineas[cursor.i];
    if (siguiente && siguiente.nivel === nivel && /^elif\s+.+:$/.test(siguiente.texto)) {
      // `elif` es un condicional anidado dentro del `else`, que es exactamente
      // lo que significa en Python.
      const anidado = unaSentencia(lineas, cursor, nivel, registro);
      return { ...resultado, rama_sino: { nodo: 'Secuencia', sentencias: [anidado] } };
    }
    if (siguiente && siguiente.nivel === nivel && siguiente.texto === 'else:') {
      cursor.i += 1;
      return {
        ...resultado,
        rama_sino: { nodo: 'Secuencia', sentencias: bloqueAnidado(lineas, cursor, nivel, registro, linea) },
      };
    }
    return resultado;
  }

  const mientras = texto.match(/^while\s+(.+):$/);
  if (mientras) {
    cursor.i += 1;
    const condicionTexto = mientras[1].trim();
    const cuerpo = bloqueAnidado(lineas, cursor, nivel, registro, linea);
    if (condicionTexto === 'True') {
      // `while True:` con un `if ...: break` AL FINAL es exactamente el
      // "Repetir ... Hasta Que" de los diagramas, y es lo que genera este
      // mismo traductor en sentido contrario. Reconocerlo cierra la ida y
      // vuelta; si no encaja, se rechaza en vez de inventar una condición.
      const ultima = cuerpo[cuerpo.length - 1];
      const esSalida =
        ultima?.nodo === 'Condicional' &&
        !ultima.rama_sino &&
        ultima.rama_si?.sentencias?.length === 1 &&
        ultima.rama_si.sentencias[0]?.nodo === 'Ruptura';
      if (!esSalida) {
        throw new ErrorPython(
          'Un "while True" solo se puede convertir si termina con "if <condición>: break".',
          linea,
        );
      }
      return {
        nodo: 'RepetirHasta',
        cuerpo: { nodo: 'Secuencia', sentencias: cuerpo.slice(0, -1) },
        condicion: ultima.condicion,
      };
    }
    return {
      nodo: 'BucleMientras',
      condicion: expresionDe(condicionTexto, linea),
      cuerpo: { nodo: 'Secuencia', sentencias: cuerpo },
    };
  }

  const para = texto.match(/^for\s+([A-Za-z_][A-Za-z0-9_]*)\s+in\s+range\s*\((.+)\)\s*:$/);
  if (para) {
    cursor.i += 1;
    const argumentos = repartirArgumentos(para[2], linea);
    /** @type {any} */
    let inicio;
    /** @type {any} */
    let finExclusivo;
    /** @type {any} */
    let paso = { nodo: 'Literal', valor: 1, tipo: 'entero' };
    if (argumentos.length === 1) {
      inicio = { nodo: 'Literal', valor: 0, tipo: 'entero' };
      finExclusivo = expresionDe(argumentos[0], linea);
    } else if (argumentos.length === 2) {
      inicio = expresionDe(argumentos[0], linea);
      finExclusivo = expresionDe(argumentos[1], linea);
    } else if (argumentos.length === 3) {
      inicio = expresionDe(argumentos[0], linea);
      finExclusivo = expresionDe(argumentos[1], linea);
      paso = expresionDe(argumentos[2], linea);
    } else {
      throw new ErrorPython('"range" necesita entre uno y tres valores.', linea);
    }
    // `range` es EXCLUSIVO en el límite y el bucle de los diagramas es
    // INCLUSIVO. Sin esta corrección el diagrama daría una vuelta de más.
    const pasoNegativo =
      paso.nodo === 'Literal' && typeof paso.valor === 'number' && paso.valor < 0;
    const fin = ajustarLimite(finExclusivo, pasoNegativo ? +1 : -1);
    return {
      nodo: 'BucleFinPara',
      variable: para[1],
      inicio,
      fin,
      paso,
      cuerpo: { nodo: 'Secuencia', sentencias: bloqueAnidado(lineas, cursor, nivel, registro, linea) },
    };
  }

  if (/^for\s+/.test(texto)) {
    throw new ErrorPython('Solo se pueden convertir los bucles "for" que usan "range".', linea);
  }

  const definicion = texto.match(/^def\s+([A-Za-z_][A-Za-z0-9_]*)\s*\((.*)\)\s*:$/);
  if (definicion) {
    cursor.i += 1;
    const parametros = definicion[2].trim() === ''
      ? []
      : definicion[2].split(',').map((p) => p.trim());
    for (const p of parametros) {
      if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(p)) {
        throw new ErrorPython(`El parámetro "${p}" tiene una forma que no se puede convertir.`, linea);
      }
    }
    return {
      nodo: 'DefinicionFuncion',
      nombre: definicion[1],
      parametros: parametros.map((nombre) => ({ nodo: 'Parametro', nombre, tipo: 'entero' })),
      cuerpo: { nodo: 'Secuencia', sentencias: bloqueAnidado(lineas, cursor, nivel, registro, linea) },
    };
  }

  // --- Sentencias simples ---
  cursor.i += 1;

  if (texto === 'pass') return null;
  if (texto === 'break') return { nodo: 'Ruptura' };
  if (texto === 'continue') {
    throw new ErrorPython('"continue" no existe en los diagramas.', linea);
  }

  const retorno = texto.match(/^return\b\s*(.*)$/);
  if (retorno) {
    return retorno[1].trim() === ''
      ? { nodo: 'Retorno', expresion: null }
      : { nodo: 'Retorno', expresion: expresionDe(retorno[1], linea) };
  }

  const escritura = texto.match(/^print\s*\((.*)\)$/);
  if (escritura) {
    const argumentos = repartirArgumentos(escritura[1], linea);
    if (argumentos.length === 0) {
      return { nodo: 'Salida', expresion: { nodo: 'Literal', valor: '', tipo: 'cadena' } };
    }
    if (argumentos.length > 1) {
      throw new ErrorPython(
        'Un "print" con varios valores separados por comas no se puede convertir: usa uno solo.',
        linea,
      );
    }
    return { nodo: 'Salida', expresion: expresionDe(argumentos[0], linea) };
  }

  const compuesta = texto.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*([+\-*/])=\s*(.+)$/);
  if (compuesta) {
    // `x += 1` es exactamente `x = x + 1`, y así lo entiende el diagrama.
    return {
      nodo: 'Asignacion',
      variable: compuesta[1],
      expresion: {
        nodo: 'Operacion',
        operador: compuesta[2],
        operandos: [
          { nodo: 'ReferenciaVariable', nombre: compuesta[1] },
          expresionDe(compuesta[3], linea),
        ],
      },
    };
  }

  const asignacion = texto.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*=\s*(.+)$/);
  if (asignacion && !/^[=<>!]/.test(texto.slice(asignacion[1].length).trim().slice(1))) {
    const valor = expresionDe(asignacion[2], linea);
    const lectura = comoLectura(valor);
    if (lectura.esLectura) {
      return lectura.tipo === null
        ? { nodo: 'Entrada', variable: asignacion[1] }
        : { nodo: 'Entrada', variable: asignacion[1], tipo: lectura.tipo };
    }
    return { nodo: 'Asignacion', variable: asignacion[1], expresion: valor };
  }

  // Llamada suelta como sentencia.
  const suelta = expresionDe(texto, linea);
  if (suelta?.nodo === 'Llamada') return suelta;
  throw new ErrorPython('No entiendo esta línea.', linea);
}

/**
 * Resta o suma 1 a un límite, simplificando cuando es un número.
 * @param {any} expresion
 * @param {number} delta
 */
function ajustarLimite(expresion, delta) {
  if (expresion?.nodo === 'Literal' && typeof expresion.valor === 'number') {
    return { nodo: 'Literal', valor: expresion.valor + delta, tipo: 'entero' };
  }
  return {
    nodo: 'Operacion',
    operador: delta < 0 ? '-' : '+',
    operandos: [expresion, { nodo: 'Literal', valor: Math.abs(delta), tipo: 'entero' }],
  };
}

/**
 * Reparte los argumentos de una llamada respetando paréntesis y comillas.
 * @param {string} texto
 * @param {number} linea
 * @returns {string[]}
 */
function repartirArgumentos(texto, linea) {
  /** @type {string[]} */
  const partes = [];
  let actual = '';
  let profundidad = 0;
  let comilla = '';
  for (const c of texto) {
    if (comilla) {
      actual += c;
      if (c === comilla) comilla = '';
      continue;
    }
    if (c === '"' || c === "'") { comilla = c; actual += c; continue; }
    if (c === '(' || c === '[') profundidad += 1;
    if (c === ')' || c === ']') profundidad -= 1;
    if (c === ',' && profundidad === 0) { partes.push(actual.trim()); actual = ''; continue; }
    actual += c;
  }
  if (profundidad !== 0) throw new ErrorPython('Faltan paréntesis por cerrar.', linea);
  if (actual.trim() !== '') partes.push(actual.trim());
  return partes;
}

/**
 * ¿Queda algún `Ruptura` en el árbol, a cualquier profundidad?
 *
 * Recorre TODO lo que cuelgue: sin esto solo se miraba el nivel raíz.
 * @param {unknown} nodo
 * @returns {boolean}
 */
function contieneRuptura(nodo) {
  if (Array.isArray(nodo)) return nodo.some(contieneRuptura);
  if (nodo === null || typeof nodo !== 'object') return false;
  if (/** @type {any} */ (nodo).nodo === 'Ruptura') return true;
  return Object.values(nodo).some(contieneRuptura);
}

/**
 * Traduce Python a IR-JSON.
 * @param {string} codigo
 * @returns {import('./resultado.js').Traduccion<any>}
 */
export function pythonAIr(codigo) {
  if (String(codigo ?? '').trim() === '') {
    return traduccionFallida([{ descripcion: 'No hay código Python que convertir.' }]);
  }
  const partido = partirEnLineas(codigo);
  if (!partido.ok) {
    return traduccionFallida([{ descripcion: partido.motivo, donde: `línea ${partido.linea}` }]);
  }

  const registro = crearRegistroDeMotivos();
  const cursor = { i: 0 };
  const sentencias = bloque(partido.lineas, cursor, 0, registro);

  /** @type {any[]} */
  const funciones = [];
  /** @type {any[]} */
  const cuerpo = [];
  for (const s of sentencias) {
    if (s.nodo === 'DefinicionFuncion') {
      const devuelve = tieneRetornoConValor(s.cuerpo);
      funciones.push({
        nodo: 'Funcion',
        nombre: s.nombre,
        parametros: s.parametros,
        cuerpo: s.cuerpo,
        tipo_retorno: devuelve ? 'entero' : null,
      });
      continue;
    }
    if (s.nodo === 'Ruptura') {
      registro.anotar('Hay un "break" fuera de un bucle.');
      continue;
    }
    cuerpo.push(s);
  }

  // `Ruptura` NO es un nodo de la IR: el backend lo rechaza
  // (`IrDeserializationError`) y los otros dos traductores tampoco saben
  // escribirlo. El filtro de arriba solo veía el `break` suelto en el NIVEL
  // RAÍZ; uno anidado dentro de un `while` se colaba, la traducción decía
  // "ok", el diagrama se abría con un bloque mudo y a partir de ahí
  // "Corrección" y "Ejecutar" daban 422 sin que el alumno entendiera nada.
  // Hallazgo de drift-reviewer (2026-08-07).
  //
  // La regla de DEC-107 es "traducir entero o no traducir": si queda alguno,
  // se falla y se dice por qué.
  if (contieneRuptura(cuerpo)) {
    registro.anotar(
      'Hay un "break" dentro de un bucle. El diagrama no tiene un bloque para ' +
        'salir de un bucle a mitad: reescribe la condición del bucle para que ' +
        'termine sola.',
    );
  }

  if (registro.hayProblemas()) return traduccionFallida(registro.motivos());
  return traduccionOk({
    nodo: 'Programa',
    // Python no declara variables, así que no hay `Declaracion` que recuperar.
    // Inventarlas exigiría deducir tipos, que es justo lo que este módulo no
    // hace: el diagrama resultante funciona igual sin ellas.
    declaraciones: [],
    funciones,
    cuerpo: { nodo: 'Secuencia', sentencias: cuerpo },
  });
}

/**
 * @param {any} secuencia
 * @returns {boolean}
 */
function tieneRetornoConValor(secuencia) {
  const sentencias = secuencia?.sentencias ?? [];
  for (const s of sentencias) {
    if (s?.nodo === 'Retorno' && s.expresion) return true;
    if (s?.nodo === 'Condicional') {
      if (tieneRetornoConValor(s.rama_si) || tieneRetornoConValor(s.rama_sino)) return true;
    }
    if (s?.cuerpo && tieneRetornoConValor(s.cuerpo)) return true;
  }
  return false;
}
